import React from 'react';
import { X, CheckCircle2, Calendar, User, Clock, CheckSquare } from 'lucide-react';

export default function TaskHistoryModal({ opportunity, completedTasks = [], onClose }) {
  // Sort completed tasks from newest to oldest
  const sortedTasks = [...completedTasks].sort((a, b) => {
    const timeA = new Date(a.completedAt || a.createdAt || 0).getTime();
    const timeB = new Date(b.completedAt || b.createdAt || 0).getTime();
    return timeB - timeA;
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 1100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div 
        className="card glass-card"
        style={{
          width: '650px',
          maxWidth: '100%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: 0,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.02)'
        }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.15rem' }}>
              <Clock size={20} /> Task History
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
              Completed tasks for <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{opportunity?.shortDesc || opportunity?.name || 'Opportunity'}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '1.25rem 1.5rem',
          overflowY: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          {sortedTasks.length === 0 ? (
            <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <CheckCircle2 size={40} style={{ margin: '0 auto 1rem auto', opacity: 0.3, color: 'var(--primary)' }} />
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                No Completed Tasks
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem', fontStyle: 'italic' }}>
                No completed tasks have been recorded for this opportunity yet.
              </p>
            </div>
          ) : (
            sortedTasks.map((task) => {
              const isTouch = task.type === 'weekly_touch';
              const completedDateStr = task.completedAt 
                ? new Date(task.completedAt).toLocaleString() 
                : task.createdAt 
                  ? new Date(task.createdAt).toLocaleString() 
                  : 'Timestamp unavailable';
              
              const completer = task.completedBy || task.assignedTo || 'Unknown User';

              return (
                <div
                  key={task.id}
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                      <CheckCircle2 size={18} color="#10B981" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '0.925rem', fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'line-through' }}>
                        {task.title}
                      </span>
                    </div>

                    {isTouch ? (
                      <span className="badge" style={{ fontSize: '0.7rem', backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#60A5FA', border: '1px solid rgba(59, 130, 246, 0.3)', flexShrink: 0 }}>
                        Weekly Touch
                      </span>
                    ) : (
                      <span className="badge badge-secondary" style={{ fontSize: '0.7rem', flexShrink: 0 }}>
                        Custom Task
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.78rem', color: 'var(--text-secondary)', borderTop: '1px solid rgba(255, 255, 255, 0.05)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <CheckSquare size={13} color="#10B981" />
                      <span>Completed by: <strong style={{ color: '#10B981' }}>{completer}</strong></span>
                    </span>

                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Clock size={13} />
                      <span>Date/Time: <strong>{completedDateStr}</strong></span>
                    </span>

                    {task.assignedTo && task.assignedTo !== completer && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <User size={13} />
                        <span>Originally Assigned: <strong>{task.assignedTo}</strong></span>
                      </span>
                    )}

                    {task.dueDate && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Calendar size={13} />
                        <span>Due: <strong>{task.dueDate}</strong></span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor: 'rgba(255, 255, 255, 0.01)'
        }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
